/**
 * Presentational shell for a booking member panel.
 *
 * Renders the common visual layout shared by both live booking (web) and
 * design preview (dashboard):
 *   - Avatar (image or initial placeholder)
 *   - Name + meta line (service, price, duration) + tag pills
 *   - Optional trailing element (info button, etc.)
 *   - Children slot (time slots, consultation button, etc.)
 *
 * Interactive behaviour and data-fetching remain in each app's wrapper.
 */
import React from "react";
import type { CSSProperties, ReactNode } from "react";
import {
  memberPanelStyle,
  memberPanelDimmedStyle,
  memberHeaderStyle,
  avatarStyle,
  avatarPlaceholderStyle,
  memberInfoStyle,
  memberNameStyle,
  memberMetaStyle,
} from "./bookingStyles";
import { AvailabilityDot } from "./components/AvailabilityDot";

export type MemberPanelShellProps = {
  /** Member display name */
  name: string;
  /** When set, the name renders as a link (e.g. to the member page). */
  nameHref?: string;
  /** Photo URL (falls back to initial-letter placeholder when absent) */
  photo?: string | null;
  /** Meta text below the name (service title, price, duration, etc.) */
  meta?: ReactNode;
  /** Factual availability state below price/duration metadata. */
  status?: ReactNode;
  /** Applies unavailable-provider spacing. */
  dimmed?: boolean;
  /** True only when the provider has bookable times for the current selection. */
  hasBookableSlots?: boolean;
  /** Render the semantic availability dot after the provider name. */
  showAvailabilityIndicator?: boolean;
  /** Element shown at the trailing edge of the header (e.g. info button) */
  trailing?: ReactNode;
  /** Panel body — time slots, consultation button, etc. */
  children?: ReactNode;
  /** Additional style overrides for the outer container */
  style?: CSSProperties;
};

export function MemberPanelShell({
  name,
  nameHref,
  photo,
  meta,
  status,
  dimmed = false,
  hasBookableSlots,
  showAvailabilityIndicator = false,
  trailing,
  children,
  style,
}: MemberPanelShellProps) {
  const panelStyle = dimmed ? memberPanelDimmedStyle : memberPanelStyle;
  const initial = name.charAt(0)?.toUpperCase() ?? "?";

  return (
    <div
      className="openings-member-panel"
      data-member-panel-state={dimmed ? "unavailable" : "available"}
      data-member-has-slots={
        hasBookableSlots === undefined
          ? undefined
          : hasBookableSlots
            ? "true"
            : "false"
      }
      style={style ? { ...panelStyle, ...style } : panelStyle}
    >
      {/* Header: avatar + name + trailing */}
      <div style={memberHeaderStyle}>
        {photo ? (
          <img
            className="openings-member-avatar"
            style={avatarStyle}
            src={photo}
            alt={name}
          />
        ) : (
          <div
            className="openings-member-avatar"
            style={avatarPlaceholderStyle}
          >
            {initial}
          </div>
        )}
        <div style={memberInfoStyle}>
          <div className="openings-member-name" style={memberNameStyle}>
            {nameHref ? (
              <a
                href={nameHref}
                style={{ color: "inherit", textDecoration: "none" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = "none";
                }}
              >
                {name}
              </a>
            ) : (
              name
            )}
            {showAvailabilityIndicator && hasBookableSlots ? (
              <AvailabilityDot />
            ) : null}
          </div>
          {meta != null && <div style={memberMetaStyle}>{meta}</div>}
          {status != null ? (
            <div className="openings-member-status" style={memberMetaStyle}>
              {status}
            </div>
          ) : null}
        </div>
        {trailing}
      </div>

      {/* Body: time slots, buttons, etc. */}
      {children}
    </div>
  );
}
